import { safeWindow } from "@/utils/window";

const VERBOSE_LOGGING = import.meta.env.VITE_VERBOSE_LOGGING === "true";

type LoggableValue = string | number | object | boolean;

export class Logger {
  private namespace: string;
  private isVerbose: boolean;
  private color: string;

  constructor(
    namespace: string,
    { isVerbose = VERBOSE_LOGGING, color = "#4834d4" } = {}
  ) {
    this.namespace = namespace;
    this.isVerbose = isVerbose;
    this.color = color;
  }

  private formatMessage = (message: string) => [
    ["[%c", `${this.namespace}`, "%c]: ", message].join(""),
    `color: ${this.color};`,
    "color: inherit;",
  ];

  print = (message: string, ...rest: LoggableValue[]) => {
    console.log(...this.formatMessage(message), ...rest);
  };

  verbose = (message: string, ...rest: LoggableValue[]) => {
    if (this.isVerbose) {
      this.print(message, ...rest);
    }
  };

  datadog = (
    message: string,
    metadata: unknown,
    status: "debug" | "info" | "warn" | "error" = "info"
  ) => {
    this.verbose("Sending log to Datadog", { message, metadata, status });
    safeWindow?.DD_LOGS?.logger?.log?.(message, metadata, status);
  };

  error = (error: Error | string, ...rest: LoggableValue[]) => {
    if (!this.isVerbose) return;
    const isString = typeof error === "string";
    const message = isString ? error : (error?.message ?? "Unknown error");
    console.error(
      ...this.formatMessage(message),
      ...(isString ? rest : [error, ...rest])
    );
  };
}
